import { useMemo } from "react";
import GlobalBadge from "../../../../../global/GlobalBadge";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../../../global/directory";
import { COLUMN_DEFS, COLUMN_VISIBILITY_KEY, PAGE_SIZE } from "../participantsListConstants";
import { cellValue, isTruthyBool } from "../participantsListHelpers";
import "../../../styles/VolunteerTable.css";
import "../../../styles/participantsList.css";

export default function ParticipantsListDirectorySection({
  draftFilters,
  setDraftFilters,
  valueFilterOptions,
  adminTournamentFeatures,
  columnVisibility,
  onVisibilityChange,
  visibleDataColumns,
  sortedRows,
  loading,
  sortColumn,
  sortOrder,
  onSortClick,
  page,
  totalPages,
  total,
  onPageChange,
  onViewDetails,
}) {
  const tableHeaders = useMemo(
    () => [
      ...visibleDataColumns.map((c) => ({
        key: c.id,
        label: c.sortable ? (
          <button
            type="button"
            className="vpart-sort-btn"
            onClick={() => onSortClick(c)}
          >
            {c.label}
            {sortColumn === c.id ? (
              <span className="vpart-sort-ind" aria-hidden>
                {sortOrder === "ASC" ? "▲" : "▼"}
              </span>
            ) : null}
          </button>
        ) : (
          c.label
        ),
      })),
      { key: "actions", label: "Actions", className: "vt-col-actions" },
    ],
    [visibleDataColumns, sortColumn, sortOrder, onSortClick],
  );

  const exportFilenameBase = useMemo(() => {
    const prefix = adminTournamentFeatures ? "admin" : "volunteer";
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `${prefix}-participants-${y}-${m}-${d}`;
  }, [adminTournamentFeatures]);

  const getExportData = () => {
    const cols = visibleDataColumns;
    if (!cols.length) return { headers: [], rows: [] };
    return {
      headers: cols.map((c) => c.label),
      rows: sortedRows.map((row) =>
        cols.map((col) => {
          const raw = cellValue(col.id, row);
          return raw == null ? "" : String(raw);
        }),
      ),
    };
  };

  const filters = (
    <GlobalDirectoryFiltersSection
      className="ub-filters ub-user-directory-filters"
      searchLabel="Search by student name"
      searchPlaceholder="Type student name"
      searchValue={draftFilters.student_search}
      onSearchChange={(v) =>
        setDraftFilters((f) => ({ ...f, student_search: v }))
      }
      columnLabel="Column"
      columnValue={draftFilters.column}
      onColumnChange={(v) =>
        setDraftFilters((f) => ({ ...f, column: v, value: "" }))
      }
      gridColumnDefs={COLUMN_DEFS}
      columnPlaceholderOption="Select"
      valueLabel="Value"
      valueValue={draftFilters.value}
      onValueChange={(v) => setDraftFilters((f) => ({ ...f, value: v }))}
      valueOptions={valueFilterOptions}
      valuePlaceholderOption="All"
      onClear={() =>
        setDraftFilters((f) => ({ ...f, column: "", value: "" }))
      }
      clearFilterLabel="Clear filter"
      secondaryStart={
        <GlobalDirectoryColumnPicker
          columnDefs={COLUMN_DEFS}
          visibility={columnVisibility}
          onVisibilityChange={onVisibilityChange}
          storageKey={COLUMN_VISIBILITY_KEY}
        />
      }
      secondaryEnd={
        <GlobalDirectoryExportMenu
          getExportData={getExportData}
          filenameBase={exportFilenameBase}
        />
      }
    />
  );

  const grid = (
    <GlobalDirectoryGridSection
      loading={loading}
      loadingMessage="Loading participants…"
      headers={tableHeaders}
      isEmpty={!loading && sortedRows.length === 0}
      emptyMessage="No matching registrations found."
      tableAriaLabel="Tournament participants"
      wrapClassName="vt-table-wrap"
      tableClassName="vt-table"
    >
      {sortedRows.map((row) => (
        <tr key={row.registration_id ?? row.payment_id}>
          {visibleDataColumns.map((col) => (
            <td key={col.id}>
              {col.id === "payment_verified" ? (
                <GlobalBadge
                  active={isTruthyBool(row.payment_verified)}
                  activeText="Verified"
                  inactiveText="Not verified"
                />
              ) : col.id === "registration_verified" ? (
                <GlobalBadge
                  active={isTruthyBool(row.registration_verified)}
                  activeText="Verified"
                  inactiveText="Not verified"
                />
              ) : col.id === "certificate_status_name" ||
                col.id === "certificate_template_name" ? (
                <span title={cellValue(col.id, row)}>{cellValue(col.id, row)}</span>
              ) : col.id === "student_name" ? (
                <span style={{ fontWeight: 600 }}>{cellValue(col.id, row)}</span>
              ) : (
                cellValue(col.id, row)
              )}
            </td>
          ))}
          <td className="vt-col-actions">
            <div className="vt-actions">
              <GlobalButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onViewDetails(row)}
              >
                View details
              </GlobalButton>
              <GlobalButton type="button" variant="ghost" size="sm" onClick={() => {}}>
                Upload payment proof
              </GlobalButton>
            </div>
          </td>
        </tr>
      ))}
    </GlobalDirectoryGridSection>
  );

  const pagination = (
    <GlobalDirectoryPaginationSection
      page={page}
      totalPages={totalPages}
      total={total}
      pageSize={PAGE_SIZE}
      onPageChange={onPageChange}
      label="Participants pagination"
    />
  );

  return (
    <GlobalDirectoryShell
      filters={filters}
      grid={grid}
      pagination={pagination}
      className="vpart-directory-shell"
    />
  );
}
